import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import { useSubscription } from '../../context/SubscriptionContext';
import { createNews, updateNews, getNewsById } from '../../services/newsService';
import { API_BASE_URL } from '../../config/api';
import './NewsInputPage.css';

const NewsInputPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission, planDisplayName, loading: subscriptionLoading } = useSubscription();
  const isEditMode = Boolean(id);

  const newsImageRef = useRef(null);
  const galleryInputRef = useRef(null);
  const heroInputRef = useRef(null);
  const mainImageRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (!subscriptionLoading && !hasPermission('canPostNews')) {
      setShowUpgradeModal(true);
    }
  }, [hasPermission, subscriptionLoading]);

  const [formData, setFormData] = useState({
    category: '',
    subCategory: '',
    title: '',
    subTitle: '',
    image: '',
    newsImageDesc: '',
    galleryImages: [],
    heroImages: [],
    heroDescription: '',
    mainImage: '',
    mainImageDesc: '',
    content: '',
    excerpt: '',
    tags: [],
    status: 'draft',
    metaTitle: '',
    metaDescription: '',
    authorName: 'Admin'
  });

  const categories = [
    'Industry News',
    'Projects',
    'Building Materials',
    'Safety',
    'Sustainability',
    'Technology',
    'Infrastructure',
    'Real Estate'
  ];

  const subCategoriesMap = {
    'Industry News': ['Market Updates', 'Company News', 'Industry Events', 'Policy Changes'],
    'Projects': ['Commercial', 'Residential', 'Industrial', 'Government'],
    'Building Materials': ['Cements', 'Steel', 'Wood', 'Glass', 'Concrete'],
    'Safety': ['Equipment', 'Regulations', 'Training', 'Incidents'],
    'Sustainability': ['Green Building', 'Energy Efficiency', 'Recycling', 'LEED'],
    'Technology': ['Software', 'Equipment', 'Innovation', 'BIM'],
    'Infrastructure': ['Roads', 'Bridges', 'Railways', 'Utilities'],
    'Real Estate': ['Commercial', 'Residential', 'Investment', 'Development']
  };

  useEffect(() => {
    if (isEditMode) {
      fetchNewsData();
    }
  }, [id]);

  const fetchNewsData = async () => {
    try {
      setLoading(true);
      const news = await getNewsById(id);
      setFormData({
        category: news.category || '',
        subCategory: news.subCategory || '',
        title: news.title || '',
        subTitle: news.subTitle || '',
        image: news.image || '',
        newsImageDesc: news.newsImageDesc || '',
        galleryImages: news.galleryImages ? JSON.parse(news.galleryImages) : [],
        heroImages: news.heroImages ? JSON.parse(news.heroImages) : [],
        heroDescription: news.heroDescription || '',
        mainImage: news.mainImage || '',
        mainImageDesc: news.mainImageDesc || '',
        content: news.content || '',
        excerpt: news.excerpt || '',
        tags: news.tags ? JSON.parse(news.tags) : [],
        status: news.status || 'draft',
        metaTitle: news.metaTitle || '',
        metaDescription: news.metaDescription || '',
        authorName: news.authorName || 'Admin'
      });
    } catch (err) {
      setError('Failed to load news article');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Generic image upload helper
  const uploadImage = async (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
    }
    if (file.size > 6 * 1024 * 1024) {
      throw new Error('Image size must be less than 6MB');
    }
    const fd = new FormData();
    fd.append('image', file);
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/upload/image?type=images`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload image');
    }
    const data = await response.json();
    const baseUrl = API_BASE_URL.replace('/api', '');
    return `${baseUrl}${data.imageUrl || data.url}`;
  };

  const handleNewsImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading('newsImage');
      setError(null);
      const url = await uploadImage(file);
      handleInputChange('image', url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading('');
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remaining = 10 - formData.galleryImages.length;
    if (files.length > remaining) {
      setError(`You can only add ${remaining} more gallery image(s). Maximum is 10.`);
      return;
    }
    try {
      setUploading('gallery');
      setError(null);
      const urls = [];
      for (const file of files) {
        const url = await uploadImage(file);
        urls.push(url);
      }
      handleInputChange('galleryImages', [...formData.galleryImages, ...urls]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading('');
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleHeroUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remaining = 2 - formData.heroImages.length;
    if (files.length > remaining) {
      setError(`You can only add ${remaining} more hero image(s). Maximum is 2.`);
      return;
    }
    try {
      setUploading('hero');
      setError(null);
      const urls = [];
      for (const file of files) {
        const url = await uploadImage(file);
        urls.push(url);
      }
      handleInputChange('heroImages', [...formData.heroImages, ...urls]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading('');
      if (heroInputRef.current) heroInputRef.current.value = '';
    }
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading('mainImage');
      setError(null);
      const url = await uploadImage(file);
      handleInputChange('mainImage', url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading('');
    }
  };

  const removeGalleryImage = (index) => {
    const updated = formData.galleryImages.filter((_, i) => i !== index);
    handleInputChange('galleryImages', updated);
  };

  const removeHeroImage = (index) => {
    const updated = formData.heroImages.filter((_, i) => i !== index);
    handleInputChange('heroImages', updated);
  };

  const getFileName = (url) => {
    if (!url) return 'Choose file.png';
    const parts = url.split('/');
    const name = parts[parts.length - 1];
    return name.length > 20 ? name.substring(0, 17) + '...' : name;
  };

  const handleSubmit = async (publishStatus = 'draft') => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    try {
      setSaving(true);
      const newsData = {
        ...formData,
        content: formData.content || formData.newsImageDesc || ' ',
        status: publishStatus
      };
      if (isEditMode) {
        await updateNews(id, newsData);
      } else {
        await createNews(newsData);
      }
      navigate('/admin/news');
    } catch (err) {
      setError(err.message || 'Failed to save news article');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (!formData.title.trim()) {
      setError('Please enter a title before proceeding');
      return;
    }
    if (currentStep < 2) setCurrentStep(2);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(1);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="news-input-loading">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="news-input-content">
        {/* Page Header */}
        <div className="ni-page-header">
          <h1 className="ni-page-title">News Listing</h1>
          <button className="ni-edit-btn" onClick={() => navigate('/admin/news')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.388 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Edit
          </button>
        </div>

        {/* Step Indicator */}
        <div className="ni-step-indicator">
          <div className="ni-step-container">
            <div className={`ni-step ${currentStep >= 1 ? 'completed' : ''}`} onClick={() => currentStep > 1 && setCurrentStep(1)}>
              <div className="ni-step-circle">
                {currentStep > 1 ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : '1'}
              </div>
              <span className="ni-step-label">Basic Info</span>
            </div>
            <div className={`ni-step-line ${currentStep >= 2 ? 'active' : ''}`}></div>
            <div className={`ni-step ${currentStep >= 2 ? 'completed' : ''}`}>
              <div className="ni-step-circle">
                {currentStep > 2 ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : '2'}
              </div>
              <span className="ni-step-label">Publish</span>
            </div>
          </div>
        </div>

        {error && <div className="ni-error-message">{error}</div>}

        {/* STEP 1 - Form */}
        {currentStep === 1 && (
          <div className="ni-form-content">
            {/* Basic Information - Category */}
            <div className="ni-form-section">
              <h2 className="ni-section-title">Basic Information</h2>
              <div className="ni-form-card">
                <div className="ni-form-row">
                  <div className="ni-form-group">
                    <label className="ni-form-label">Category</label>
                    <select
                      className="ni-select-field"
                      value={formData.category}
                      onChange={(e) => {
                        handleInputChange('category', e.target.value);
                        handleInputChange('subCategory', '');
                      }}
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ni-form-group">
                    <label className="ni-form-label">Sub category</label>
                    <select
                      className="ni-select-field"
                      value={formData.subCategory}
                      onChange={(e) => handleInputChange('subCategory', e.target.value)}
                      disabled={!formData.category}
                    >
                      <option value="">{formData.category ? 'Select sub-category' : 'Select a category first'}</option>
                      {(subCategoriesMap[formData.category] || []).map(sc => (
                        <option key={sc} value={sc}>{sc}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Basic Information - Title */}
            <div className="ni-form-section">
              <h2 className="ni-section-title">Basic Information</h2>
              <div className="ni-form-card">
                <div className="ni-form-group">
                  <label className="ni-form-label">News Title</label>
                  <input
                    type="text"
                    className="ni-text-input"
                    placeholder="Project Title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                  />
                </div>
                <div className="ni-form-group">
                  <label className="ni-form-label">Sub Title</label>
                  <input
                    type="text"
                    className="ni-text-input"
                    placeholder="Sub title"
                    value={formData.subTitle}
                    onChange={(e) => handleInputChange('subTitle', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Image Section */}
            <div className="ni-form-section">
              <h2 className="ni-section-title">Image</h2>
              <div className="ni-form-card">

                {/* News Image */}
                <div className="ni-image-block">
                  <h3 className="ni-image-block-title">News Image</h3>
                  {formData.image && (
                    <div className="ni-file-chip-row">
                      <span className="ni-file-chip">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.91667 4.66667L7 1.75L4.08334 4.66667" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 1.75V8.75" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        {getFileName(formData.image)}
                      </span>
                      <button className="ni-delete-chip-btn" onClick={() => handleInputChange('image', '')} title="Remove">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1.75 3.5H12.25" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4.66667 3.5V2.33333C4.66667 2.02391 4.78958 1.72717 5.00838 1.50838C5.22717 1.28958 5.52391 1.16667 5.83333 1.16667H8.16667C8.47609 1.16667 8.77283 1.28958 8.99162 1.50838C9.21042 1.72717 9.33333 2.02391 9.33333 2.33333V3.5M11.0833 3.5V11.6667C11.0833 11.9761 10.9604 12.2728 10.7416 12.4916C10.5228 12.7104 10.2261 12.8333 9.91667 12.8333H4.08333C3.77391 12.8333 3.47717 12.7104 3.25838 12.4916C3.03958 12.2728 2.91667 11.9761 2.91667 11.6667V3.5H11.0833Z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                  )}
                  <div className="ni-upload-row">
                    <span className="ni-upload-label-text">Upload</span>
                    <label className="ni-choose-file-btn">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.91667 4.66667L7 1.75L4.08334 4.66667" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 1.75V8.75" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Choose file
                      <input ref={newsImageRef} type="file" accept="image/*" onChange={handleNewsImageUpload} style={{ display: 'none' }} disabled={uploading === 'newsImage'} />
                    </label>
                    <span className="ni-upload-hint-text">{uploading === 'newsImage' ? 'Uploading...' : 'Upload any attachment'}</span>
                  </div>
                  <p className="ni-upload-size-hint">Upload any file type. Maximum file size: 6MB</p>

                  <div className="ni-form-group" style={{ marginTop: 16 }}>
                    <label className="ni-form-label">Description</label>
                    <textarea className="ni-textarea-input" placeholder="Write a short description" rows={4} value={formData.newsImageDesc} onChange={(e) => handleInputChange('newsImageDesc', e.target.value)}></textarea>
                  </div>
                </div>

                {/* Gallery */}
                <div className="ni-image-block">
                  <h3 className="ni-image-block-title">Gallery <span className="ni-image-max-hint">( Maximum 10 images)</span></h3>
                  {formData.galleryImages.map((img, idx) => (
                    <div className="ni-file-chip-row" key={idx}>
                      <span className="ni-file-chip">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.91667 4.66667L7 1.75L4.08334 4.66667" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 1.75V8.75" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        {getFileName(img)}
                      </span>
                      <button className="ni-delete-chip-btn" onClick={() => removeGalleryImage(idx)} title="Remove">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1.75 3.5H12.25" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4.66667 3.5V2.33333C4.66667 2.02391 4.78958 1.72717 5.00838 1.50838C5.22717 1.28958 5.52391 1.16667 5.83333 1.16667H8.16667C8.47609 1.16667 8.77283 1.28958 8.99162 1.50838C9.21042 1.72717 9.33333 2.02391 9.33333 2.33333V3.5M11.0833 3.5V11.6667C11.0833 11.9761 10.9604 12.2728 10.7416 12.4916C10.5228 12.7104 10.2261 12.8333 9.91667 12.8333H4.08333C3.77391 12.8333 3.47717 12.7104 3.25838 12.4916C3.03958 12.2728 2.91667 11.9761 2.91667 11.6667V3.5H11.0833Z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                  ))}
                  {formData.galleryImages.length < 10 && (
                    <div className="ni-upload-row">
                      <span className="ni-upload-label-text">Upload</span>
                      <label className="ni-choose-file-btn">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.91667 4.66667L7 1.75L4.08334 4.66667" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 1.75V8.75" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        Choose file
                        <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={handleGalleryUpload} style={{ display: 'none' }} disabled={uploading === 'gallery'} />
                      </label>
                      <span className="ni-upload-hint-text">{uploading === 'gallery' ? 'Uploading...' : 'Upload any attachment'}</span>
                    </div>
                  )}
                  <p className="ni-upload-size-hint">Upload any file type. Maximum file size: 6MB</p>
                </div>

                {/* Here 2 images */}
                <div className="ni-image-block">
                  <h3 className="ni-image-block-title">Here 2 images <span className="ni-image-max-hint">( Maximum 2 images)</span></h3>
                  {formData.heroImages.map((img, idx) => (
                    <div className="ni-file-chip-row" key={idx}>
                      <span className="ni-file-chip">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.91667 4.66667L7 1.75L4.08334 4.66667" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 1.75V8.75" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        {getFileName(img)}
                      </span>
                      <button className="ni-delete-chip-btn" onClick={() => removeHeroImage(idx)} title="Remove">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1.75 3.5H12.25" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4.66667 3.5V2.33333C4.66667 2.02391 4.78958 1.72717 5.00838 1.50838C5.22717 1.28958 5.52391 1.16667 5.83333 1.16667H8.16667C8.47609 1.16667 8.77283 1.28958 8.99162 1.50838C9.21042 1.72717 9.33333 2.02391 9.33333 2.33333V3.5M11.0833 3.5V11.6667C11.0833 11.9761 10.9604 12.2728 10.7416 12.4916C10.5228 12.7104 10.2261 12.8333 9.91667 12.8333H4.08333C3.77391 12.8333 3.47717 12.7104 3.25838 12.4916C3.03958 12.2728 2.91667 11.9761 2.91667 11.6667V3.5H11.0833Z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                  ))}
                  {formData.heroImages.length < 2 && (
                    <div className="ni-upload-row">
                      <span className="ni-upload-label-text">Upload</span>
                      <label className="ni-choose-file-btn">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.91667 4.66667L7 1.75L4.08334 4.66667" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 1.75V8.75" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        Choose file
                        <input ref={heroInputRef} type="file" accept="image/*" multiple onChange={handleHeroUpload} style={{ display: 'none' }} disabled={uploading === 'hero'} />
                      </label>
                      <span className="ni-upload-hint-text">{uploading === 'hero' ? 'Uploading...' : 'Upload any attachment'}</span>
                    </div>
                  )}
                  <p className="ni-upload-size-hint">Upload any file type. Maximum file size: 6MB</p>
                </div>

                {/* Description for hero section */}
                <div className="ni-image-block">
                  <div className="ni-form-group">
                    <label className="ni-form-label">Description</label>
                    <textarea className="ni-textarea-input" placeholder="Write a short description" rows={4} value={formData.heroDescription} onChange={(e) => handleInputChange('heroDescription', e.target.value)}></textarea>
                  </div>
                </div>

                {/* News main image */}
                <div className="ni-image-block">
                  <h3 className="ni-image-block-title">News main image</h3>
                  {formData.mainImage && (
                    <div className="ni-file-chip-row">
                      <span className="ni-file-chip">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.91667 4.66667L7 1.75L4.08334 4.66667" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 1.75V8.75" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        {getFileName(formData.mainImage)}
                      </span>
                      <button className="ni-delete-chip-btn" onClick={() => handleInputChange('mainImage', '')} title="Remove">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1.75 3.5H12.25" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4.66667 3.5V2.33333C4.66667 2.02391 4.78958 1.72717 5.00838 1.50838C5.22717 1.28958 5.52391 1.16667 5.83333 1.16667H8.16667C8.47609 1.16667 8.77283 1.28958 8.99162 1.50838C9.21042 1.72717 9.33333 2.02391 9.33333 2.33333V3.5M11.0833 3.5V11.6667C11.0833 11.9761 10.9604 12.2728 10.7416 12.4916C10.5228 12.7104 10.2261 12.8333 9.91667 12.8333H4.08333C3.77391 12.8333 3.47717 12.7104 3.25838 12.4916C3.03958 12.2728 2.91667 11.9761 2.91667 11.6667V3.5H11.0833Z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                  )}
                  <div className="ni-upload-row">
                    <span className="ni-upload-label-text">Upload</span>
                    <label className="ni-choose-file-btn">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.91667 4.66667L7 1.75L4.08334 4.66667" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 1.75V8.75" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Choose file
                      <input ref={mainImageRef} type="file" accept="image/*" onChange={handleMainImageUpload} style={{ display: 'none' }} disabled={uploading === 'mainImage'} />
                    </label>
                    <span className="ni-upload-hint-text">{uploading === 'mainImage' ? 'Uploading...' : 'Upload any attachment'}</span>
                  </div>
                  <p className="ni-upload-size-hint">Upload any file type. Maximum file size: 6MB</p>

                  <div className="ni-form-group" style={{ marginTop: 16 }}>
                    <label className="ni-form-label">Description</label>
                    <textarea className="ni-textarea-input" placeholder="Write a short description" rows={4} value={formData.mainImageDesc} onChange={(e) => handleInputChange('mainImageDesc', e.target.value)}></textarea>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Button */}
            <div className="ni-form-actions">
              <button className="ni-next-btn" onClick={handleNext}>Next</button>
            </div>
          </div>
        )}

        {/* STEP 2 - Preview & Publish */}
        {currentStep === 2 && (
          <div className="ni-form-content">
            <div className="ni-publish-preview">
              <h2 className="ni-preview-main-title">{formData.title || 'Untitled News'}</h2>
              {formData.subTitle && <p className="ni-preview-subtitle">{formData.subTitle}</p>}

              {formData.image && (
                <div className="ni-preview-hero-image">
                  <img src={formData.image} alt="News" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}

              {formData.newsImageDesc && <p className="ni-preview-description">{formData.newsImageDesc}</p>}

              {formData.galleryImages.length > 0 && (
                <div className="ni-preview-gallery">
                  {formData.galleryImages.slice(0, 6).map((img, idx) => (
                    <div className="ni-preview-gallery-thumb" key={idx}>
                      <img src={img} alt={`Gallery ${idx + 1}`} onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  ))}
                  {formData.galleryImages.length > 6 && (
                    <div className="ni-preview-gallery-thumb ni-gallery-more">
                      <span>+{formData.galleryImages.length - 6}</span>
                    </div>
                  )}
                </div>
              )}

              {formData.heroImages.map((img, idx) => (
                <div className="ni-preview-hero-image" key={idx}>
                  <img src={img} alt={`Hero ${idx + 1}`} onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              ))}

              {formData.heroDescription && <p className="ni-preview-description">{formData.heroDescription}</p>}

              {formData.mainImage && (
                <div className="ni-preview-hero-image">
                  <img src={formData.mainImage} alt="Main" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}

              {formData.mainImageDesc && <p className="ni-preview-description">{formData.mainImageDesc}</p>}
            </div>

            <div className="ni-form-actions ni-form-actions-publish">
              <button className="ni-cancel-btn" onClick={handleBack}>Cancel</button>
              <button className="ni-publish-btn" onClick={() => handleSubmit('published')} disabled={saving}>
                {saving ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        )}
      </div>

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => { setShowUpgradeModal(false); navigate(-1); }}
        feature="Post News"
        currentPlan={planDisplayName || 'Basic'}
        requiredPlan="GCC Business"
      />
    </AdminLayout>
  );
};

export default NewsInputPage;
